import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import './App.css';

/* ===================== CONSTANTS ===================== */
const HEADER_BLUE = "#336699";
const LINK_BLUE = "#5599cc";
const MYSPACE_ORANGE = "#ff6600";
const TEXT_DARK = "#003366";
const TEXT_BODY = "#333";
const BORDER_BLUE = "#6699cc";
const LIGHT_BLUE = "#c0ddf5";
const COMMENT_BG = "#eef5fc";
const PROFILE_BG = "#d4e9ff";
const WHITE = "#fff";
const GLITTER_PINK = "#ff69b4";
const BG_DARK = "#003366";

const REACTIONS = [
  { emoji: "💙", label: "Love" },
  { emoji: "🙏", label: "Praying" },
  { emoji: "💪", label: "Strength" },
  { emoji: "🌊", label: "Riding the wave" },
  { emoji: "🤗", label: "Hugs" },
];

const DEFAULT_SETTINGS = {
  site_name: "Waves Upon Waves",
  subtitle: "A place for updates in McKenna's battle with cancer",
  marquee: "🌊 ✨ Riding the waves ~ God is good ~ One day at a time ~ 💙🙏 ~ Thank you for being here ~ You are my people ~ 🌊 ✨",
  profile_emoji: "🌊",
  display_name: "McKenna",
  tagline: '"Riding the waves"',
  location: "Chapel Hill, NC",
  bio: "Hey y'all! 💙 This is my little corner of the internet where I'm keeping everyone updated on my journey. Think of it like a group text but better — no reply-all chaos! 😅\n\nDrop a reaction, leave a comment, or just check in whenever you think of me. Every prayer and good vibe matters more than you know. 🌊🙏\n\n~*~ Love you all ~*~",
  song_title: "Oceans (Where Feet May Fail)",
  song_artist: "Hillsong UNITED",
  song_url: "https://open.spotify.com/track/2dLLR6qlu5UJ5gk0dKz0h3",
  blinkies: "Fighter, Faith, 🌊 Waves",
};

const SAMPLE_POSTS = [
  { id: 1, type: "update", title: "Round 3 done \u2713", content: "Finished my third round of chemo yesterday. Today is a couch day and that\u2019s okay.", created_at: "2026-04-12T14:34:00Z", reactions: { "\uD83D\uDC99": 24, "\uD83D\uDE4F": 18, "\uD83D\uDCAA": 31, "\uD83C\uDF0A": 14, "\uD83E\uDD17": 12 }, pinned: true },
  { id: 2, type: "photo", title: "Small victories", content: "First walk around the block in two weeks.", created_at: "2026-04-10T11:15:00Z", reactions: { "\uD83D\uDC99": 42, "\uD83D\uDE4F": 5, "\uD83D\uDCAA": 28, "\uD83C\uDF0A": 22, "\uD83E\uDD17": 15 }, pinned: false },
];

function Linkify({ children }) {
  if (typeof children !== "string") return children;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = children.split(urlRegex);
  return parts.map((part, i) =>
    urlRegex.test(part)
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color: LINK_BLUE, wordBreak: "break-all" }}>{part}</a>
      : part
  );
}

/* ===================== HELPER COMPONENTS ===================== */
function MarqueeText({ children }) {
  return (
    <div style={{ overflow: "hidden", whiteSpace: "nowrap", width: "100%" }}>
      <div className="marquee-text">
        {children}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{children}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{children}
      </div>
    </div>
  );
}

function Blinkie({ text, bg, color }) {
  return (
    <span className="blinkie" style={{ background: bg, color: color, borderColor: bg }}>
      {text}
    </span>
  );
}

function SectionBox({ title, children, color = HEADER_BLUE }) {
  return (
    <div style={{ border: `2px solid ${color}`, marginBottom: 12, background: WHITE }}>
      <div style={{ background: color, padding: "4px 8px", fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, fontWeight: 700, color: WHITE, textTransform: "uppercase", letterSpacing: 0.5 }}>{title}</div>
      <div style={{ padding: "10px 12px" }}>{children}</div>
    </div>
  );
}

function MusicPlayer({ settings }) {
  // Extract Spotify track ID from URL like https://open.spotify.com/track/2dLLR6qlu5UJ5gk0dKz0h3
  const getSpotifyId = (url) => {
    if (!url) return null;
    const match = url.match(/track\/([a-zA-Z0-9]+)/);
    return match ? match[1] : null;
  };
  const trackId = getSpotifyId(settings.song_url);

  return (
    <div style={{ background: "#1a1a2e", border: `2px inset ${BORDER_BLUE}`, padding: 8 }}>
      {trackId ? (
        <iframe
          title="Spotify Player"
          src={`https://open.spotify.com/embed/track/${trackId}?utm_source=generator&theme=0`}
          width="100%"
          height="80"
          frameBorder="0"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
          style={{ borderRadius: 8 }}
        />
      ) : (
        <div style={{ textAlign: "center", padding: 8 }}>
          <div style={{ fontFamily: "'Courier New', monospace", fontSize: 11, color: "#0f0" }}>{settings.song_title}</div>
          <div style={{ fontFamily: "'Courier New', monospace", fontSize: 10, color: "#0a0" }}>{settings.song_artist}</div>
          {settings.song_url && (
            <a href={settings.song_url} target="_blank" rel="noopener noreferrer"
              style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: "#1DB954", marginTop: 6, display: "inline-block" }}>Listen on Spotify</a>
          )}
        </div>
      )}
    </div>
  );
}

/* ===================== REACTIONS ===================== */
function ReactionBar({ reactions, postId, onReact }) {
  const [reacted, setReacted] = useState({});

  const toggle = async (emoji) => {
    const wasReacted = reacted[emoji];
    setReacted(prev => {
      const next = { ...prev };
      if (wasReacted) delete next[emoji];
      else next[emoji] = true;
      return next;
    });
    // Update in database
    const currentCount = reactions[emoji] || 0;
    const newCount = wasReacted ? Math.max(0, currentCount - 1) : currentCount + 1;
    const newReactions = { ...reactions, [emoji]: newCount };
    if (postId && supabase) {
      await supabase.from('posts').update({ reactions: newReactions }).eq('id', postId);
    }
    if (onReact) onReact(newReactions);
  };

  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 8 }}>
      {REACTIONS.map(({ emoji, label }) => {
        const count = reactions[emoji] || 0;
        const active = reacted[emoji];
        return (
          <button key={emoji} onClick={() => toggle(emoji)} title={label}
            style={{ display: "flex", alignItems: "center", gap: 3, padding: "2px 8px", border: active ? `2px inset ${MYSPACE_ORANGE}` : `2px outset ${BORDER_BLUE}`, background: active ? "#fff3e0" : LIGHT_BLUE, cursor: "pointer", fontSize: 12, fontFamily: "'Trebuchet MS', sans-serif" }}>
            <span>{emoji}</span><span style={{ fontWeight: 700, color: active ? MYSPACE_ORANGE : TEXT_DARK }}>{count}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ===================== COMMENTS ===================== */
function CommentSection({ postId }) {
  const [comments, setComments] = useState([]);
  const [show, setShow] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [name, setName] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (show && !loaded && postId) {
      loadComments();
    }
  }, [show]);

  const loadComments = async () => {
    if (!supabase) return;
    const { data } = await supabase
      .from('comments')
      .select('*')
      .eq('post_id', postId)
      .order('created_at', { ascending: true });
    if (data) setComments(data);
    setLoaded(true);
  };

  const submit = async () => {
    if (!newComment.trim() || !name.trim()) return;
    const initials = name.trim().split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
    const comment = {
      post_id: postId,
      name: name.trim(),
      text: newComment.trim(),
      avatar: initials,
    };
    if (supabase) {
      const { data } = await supabase.from('comments').insert(comment).select();
      if (data) setComments([...comments, data[0]]);
    } else {
      setComments([...comments, { ...comment, id: Date.now(), created_at: new Date().toISOString() }]);
    }
    setNewComment("");
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return "Just now";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div style={{ marginTop: 8, borderTop: `1px dashed ${BORDER_BLUE}`, paddingTop: 6 }}>
      <span onClick={() => setShow(!show)}
        style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: LINK_BLUE, cursor: "pointer", textDecoration: "underline", fontWeight: 700 }}>
        💬 {comments.length > 0 ? `${comments.length} Comment${comments.length !== 1 ? "s" : ""}` : "Leave a Comment"} {show ? "▲" : "▼"}
      </span>
      {show && (
        <div style={{ marginTop: 8 }}>
          {comments.map(c => (
            <div key={c.id} style={{ display: "flex", gap: 8, marginBottom: 8, padding: 6, background: COMMENT_BG, border: `1px solid ${BORDER_BLUE}` }}>
              <div style={{ width: 30, height: 30, border: `1px solid ${BORDER_BLUE}`, background: PROFILE_BG, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Trebuchet MS', sans-serif", fontSize: 10, fontWeight: 800, color: HEADER_BLUE, flexShrink: 0 }}>{c.avatar}</div>
              <div style={{ flex: 1 }}>
                <div><span style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, fontWeight: 700, color: LINK_BLUE }}>{c.name}</span> <span style={{ fontSize: 10, color: "#999" }}>{timeAgo(c.created_at)}</span></div>
                <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, color: TEXT_BODY, marginTop: 2, lineHeight: 1.4 }}>{c.text}</div>
              </div>
            </div>
          ))}
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
            <input type="text" placeholder="Your Name" value={name} onChange={e => setName(e.target.value)}
              style={{ padding: "4px 6px", border: `2px inset ${BORDER_BLUE}`, fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12 }} />
            <div style={{ display: "flex", gap: 4 }}>
              <input type="text" placeholder="Add a comment..." value={newComment} onChange={e => setNewComment(e.target.value)} onKeyDown={e => { if (e.key === "Enter") submit(); }}
                style={{ flex: 1, padding: "4px 6px", border: `2px inset ${BORDER_BLUE}`, fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12 }} />
              <button onClick={submit}
                style={{ padding: "4px 12px", border: `2px outset ${HEADER_BLUE}`, background: HEADER_BLUE, color: WHITE, fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Post</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ===================== BULLETIN POST ===================== */
function BulletinPost({ post, onUpdate, isAdmin, onDelete, onPin }) {
  const [expanded, setExpanded] = useState(false);
  const [reactions, setReactions] = useState(post.reactions || {});
  const isLong = post.content.length > 250;
  const displayContent = isLong && !expanded ? post.content.slice(0, 250) + "..." : post.content;
  const typeLabel = { update: "BULLETIN", photo: "PHOTO UPDATE", support: "WAYS TO SUPPORT" };
  const typeColor = { update: HEADER_BLUE, photo: "#339966", support: "#cc3366" };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) + ' ' +
           d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div style={{ border: `2px solid ${typeColor[post.type] || HEADER_BLUE}`, background: WHITE, marginBottom: 10 }}>
      <div style={{ background: typeColor[post.type] || HEADER_BLUE, padding: "3px 8px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
        <span style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, fontWeight: 700, color: WHITE, textTransform: "uppercase", letterSpacing: 0.5 }}>
          {post.pinned && "📌 "}{typeLabel[post.type]}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 10, color: "rgba(255,255,255,0.8)" }}>{formatDate(post.created_at)}</span>
          {isAdmin && onPin && (
            <button
              onClick={() => onPin(post.id, !post.pinned)}
              title={post.pinned ? "Unpin post" : "Pin to top"}
              style={{ background: post.pinned ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)", border: "1px solid rgba(255,255,255,0.5)", color: WHITE, fontSize: 10, fontWeight: 700, padding: "0 5px", cursor: "pointer", lineHeight: 1.4 }}>
              📌
            </button>
          )}
          {isAdmin && onDelete && (
            <button
              onClick={() => {
                if (window.confirm(`Delete this post?\n\n"${post.title}"\n\nThis can't be undone.`)) {
                  onDelete(post.id);
                }
              }}
              title="Delete post"
              style={{ background: "rgba(0,0,0,0.25)", border: "1px solid rgba(255,255,255,0.5)", color: WHITE, fontSize: 10, fontWeight: 700, padding: "0 5px", cursor: "pointer", lineHeight: 1.4 }}>
              ✕
            </button>
          )}
        </span>
      </div>
      <div style={{ padding: "8px 10px" }}>
        <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 14, fontWeight: 700, color: TEXT_DARK, marginBottom: 4 }}>{post.title}</div>
        {post.image_url && (
          <img src={post.image_url} alt={post.title} style={{ maxWidth: "100%", display: "block", marginBottom: 8, border: `2px solid ${BORDER_BLUE}` }} />
        )}
        <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, color: TEXT_BODY, lineHeight: 1.6, whiteSpace: "pre-line" }}>
          <Linkify>{displayContent}</Linkify>
          {isLong && <span onClick={() => setExpanded(!expanded)} style={{ color: LINK_BLUE, cursor: "pointer", textDecoration: "underline", fontWeight: 700, marginLeft: 4, fontSize: 11 }}>[{expanded ? "less" : "more"}]</span>}
        </div>
        <ReactionBar reactions={reactions} postId={post.id} onReact={(newReactions) => { setReactions(newReactions); if (onUpdate) onUpdate(post.id, newReactions); }} />
        <CommentSection postId={post.id} />
      </div>
    </div>
  );
}

/* ===================== COMPOSE MODAL ===================== */
function ComposeModal({ onClose, onPost }) {
  const [postType, setPostType] = useState("update");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [notifySubscribers, setNotifySubscribers] = useState(true);
  const [posting, setPosting] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const types = [{ key: "update", label: "Bulletin" }, { key: "photo", label: "Photo" }, { key: "support", label: "Support" }];

  const resizeImage = (file, maxDim = 1200) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const ratio = Math.min(maxDim / width, maxDim / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        if (blob) resolve(new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" }));
        else reject(new Error("Conversion failed"));
      }, "image/jpeg", 0.85);
    };
    img.onerror = () => reject(new Error("Could not load image"));
    img.src = URL.createObjectURL(file);
  });

  const handlePhotoSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadError("");
    try {
      const resized = await resizeImage(file);
      setPhotoFile(resized);
      setPhotoPreview(URL.createObjectURL(resized));
    } catch {
      setUploadError("Could not process this image. Try a JPG or PNG.");
    }
  };

  const postingRef = useRef(false);
  const handlePost = async () => {
    if (!title || posting || postingRef.current) return;
    if (postType === "photo" && !photoFile && !content) return;
    postingRef.current = true;
    setPosting(true);
    let imageUrl = null;
    if (photoFile) {
      const fileName = `${Date.now()}-${photoFile.name}`;
      const { error } = await supabase.storage.from("photos").upload(fileName, photoFile);
      if (error) {
        setUploadError("Photo upload failed. Please try again.");
        postingRef.current = false;
        setPosting(false);
        return;
      }
      const { data: urlData } = supabase.storage.from("photos").getPublicUrl(fileName);
      imageUrl = urlData.publicUrl;
    }
    await onPost({ type: postType, title, content, notify: notifySubscribers ? "email" : null, imageUrl });
    setPosting(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }} onClick={onClose}>
      <div style={{ background: PROFILE_BG, border: `3px outset ${HEADER_BLUE}`, maxWidth: 480, width: "100%", maxHeight: "80vh", overflow: "auto" }} onClick={e => e.stopPropagation()}>
        <div style={{ background: HEADER_BLUE, padding: "6px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, fontWeight: 700, color: WHITE }}>📝 POST NEW BULLETIN</span>
          <button onClick={onClose} style={{ background: "#cc0000", border: `2px outset #cc0000`, color: WHITE, fontWeight: 800, cursor: "pointer", padding: "1px 6px", fontSize: 12 }}>X</button>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, fontWeight: 700, color: TEXT_DARK, marginBottom: 4 }}>TYPE:</div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {types.map(t => (
                <button key={t.key} onClick={() => setPostType(t.key)}
                  style={{ padding: "4px 10px", border: postType === t.key ? `2px inset ${MYSPACE_ORANGE}` : `2px outset ${BORDER_BLUE}`, background: postType === t.key ? "#fff3e0" : LIGHT_BLUE, fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, fontWeight: 700, color: postType === t.key ? MYSPACE_ORANGE : TEXT_DARK, cursor: "pointer" }}>{t.label}</button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, fontWeight: 700, color: TEXT_DARK, marginBottom: 2 }}>SUBJECT:</div>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
              style={{ width: "100%", padding: "4px 6px", border: `2px inset ${BORDER_BLUE}`, fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, boxSizing: "border-box" }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, fontWeight: 700, color: TEXT_DARK, marginBottom: 2 }}>{postType === "photo" ? "CAPTION (optional):" : "BODY:"}</div>
            <textarea value={content} onChange={e => setContent(e.target.value)} rows={postType === "photo" ? 2 : 6}
              style={{ width: "100%", padding: "4px 6px", border: `2px inset ${BORDER_BLUE}`, fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, resize: "vertical", lineHeight: 1.5, boxSizing: "border-box" }} />
          </div>
          {(postType === "photo" || postType === "update") && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, fontWeight: 700, color: TEXT_DARK, marginBottom: 4 }}>{postType === "photo" ? "PHOTO:" : "PHOTO (optional):"}</div>
              <label style={{ display: "block", padding: "8px 12px", border: `2px dashed ${BORDER_BLUE}`, background: LIGHT_BLUE, textAlign: "center", cursor: "pointer", fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, color: TEXT_DARK }}>
                📷 {photoFile ? photoFile.name : "Click to choose a photo"}
                <input type="file" accept="image/*" onChange={handlePhotoSelect} style={{ display: "none" }} />
              </label>
              {photoPreview && (
                <img src={photoPreview} alt="Preview" style={{ marginTop: 8, maxWidth: "100%", maxHeight: 200, display: "block", border: `2px solid ${BORDER_BLUE}` }} />
              )}
              {uploadError && <div style={{ color: "#cc0000", fontSize: 11, marginTop: 4, fontFamily: "'Trebuchet MS', sans-serif" }}>{uploadError}</div>}
            </div>
          )}
          <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, fontWeight: 700, color: TEXT_DARK }}>
              <input type="checkbox" checked={notifySubscribers} onChange={e => setNotifySubscribers(e.target.checked)} />
              📧 Email subscribers about this post
            </label>
          </div>
          <button onClick={handlePost} disabled={posting}
            style={{ width: "100%", padding: 8, border: `2px outset ${title && content ? MYSPACE_ORANGE : BORDER_BLUE}`, background: title && content ? MYSPACE_ORANGE : LIGHT_BLUE, color: title && content ? WHITE : "#999", fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, fontWeight: 800, cursor: title && content ? "pointer" : "default", textTransform: "uppercase", letterSpacing: 1 }}>
            {posting ? "Posting..." : "🌊 Post Bulletin"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===================== ADMIN PANEL ===================== */
function AdminField({ label, field, multiline, placeholder, value, onChange }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, fontWeight: 700, color: TEXT_DARK, marginBottom: 3, textTransform: "uppercase" }}>{label}:</div>
      {multiline ? (
        <textarea value={value || ""} onChange={e => onChange(field, e.target.value)} rows={4} placeholder={placeholder}
          style={{ width: "100%", padding: "4px 6px", border: `2px inset ${BORDER_BLUE}`, fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, resize: "vertical", lineHeight: 1.5, boxSizing: "border-box" }} />
      ) : (
        <input type="text" value={value || ""} onChange={e => onChange(field, e.target.value)} placeholder={placeholder}
          style={{ width: "100%", padding: "4px 6px", border: `2px inset ${BORDER_BLUE}`, fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, boxSizing: "border-box" }} />
      )}
    </div>
  );
}

function AdminPanel({ settings, onSave, onClose }) {
  const [s, setS] = useState({ ...settings });
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [profileUploading, setProfileUploading] = useState(false);
  const [profileUploadError, setProfileUploadError] = useState("");

  const update = (key, val) => setS(prev => ({ ...prev, [key]: val }));

  const handleProfilePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProfileUploading(true);
    setProfileUploadError("");
    const fileName = `profile-${Date.now()}-${file.name}`;
    const { error } = await supabase.storage.from("photos").upload(fileName, file);
    if (error) {
      setProfileUploadError("Upload failed. Please try again.");
      setProfileUploading(false);
      return;
    }
    const { data: urlData } = supabase.storage.from("photos").getPublicUrl(fileName);
    update("profile_photo", urlData.publicUrl);
    setProfileUploading(false);
  };

  const handleSave = async () => {
    await onSave(s);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const tabs = [
    { key: "profile", label: "👤 Profile" },
    { key: "page", label: "📄 Page" },
    { key: "song", label: "🎵 Song" },
    { key: "appts", label: "📅 Appts" },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }} onClick={onClose}>
      <div style={{ background: PROFILE_BG, border: `3px outset ${HEADER_BLUE}`, maxWidth: 540, width: "100%", maxHeight: "85vh", overflow: "auto" }} onClick={e => e.stopPropagation()}>
        <div style={{ background: HEADER_BLUE, padding: "6px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, fontWeight: 700, color: WHITE }}>⚙️ EDIT MY PAGE</span>
          <button onClick={onClose} style={{ background: "#cc0000", border: `2px outset #cc0000`, color: WHITE, fontWeight: 800, cursor: "pointer", padding: "1px 6px", fontSize: 12 }}>X</button>
        </div>
        <div style={{ display: "flex", borderBottom: `2px solid ${HEADER_BLUE}`, background: LIGHT_BLUE }}>
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{ flex: 1, padding: "6px 4px", border: "none", borderBottom: activeTab === tab.key ? `3px solid ${MYSPACE_ORANGE}` : "3px solid transparent", background: activeTab === tab.key ? WHITE : "transparent", fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, fontWeight: 700, color: activeTab === tab.key ? MYSPACE_ORANGE : TEXT_DARK, cursor: "pointer" }}>{tab.label}</button>
          ))}
        </div>
        <div style={{ padding: 16 }}>
          {activeTab === "profile" && (
            <>
              <AdminField label="Display Name" field="display_name" value={s.display_name} onChange={update} />
              <AdminField label="Tagline / Status" field="tagline" value={s.tagline} onChange={update} />
              <AdminField label="Location" field="location" value={s.location} onChange={update} />
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, fontWeight: 700, color: TEXT_DARK, marginBottom: 3, textTransform: "uppercase" }}>PROFILE EMOJI:</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {["🌊", "💙", "⚓", "🦋", "🌸", "✝️", "💪", "🌅", "🐚", "🎗️"].map(e => (
                    <button key={e} onClick={() => update("profile_emoji", e)}
                      style={{ width: 36, height: 36, border: s.profile_emoji === e ? `3px inset ${MYSPACE_ORANGE}` : `2px outset ${BORDER_BLUE}`, background: s.profile_emoji === e ? "#fff3e0" : WHITE, cursor: "pointer", fontSize: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>{e}</button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, fontWeight: 700, color: TEXT_DARK, marginBottom: 4, textTransform: "uppercase" }}>Profile Photo:</div>
                {s.profile_photo && (
                  <img src={s.profile_photo} alt="Profile" style={{ width: 80, height: 80, objectFit: "cover", display: "block", marginBottom: 6, border: `2px solid ${BORDER_BLUE}` }} />
                )}
                <label style={{ display: "block", padding: "6px 10px", border: `2px dashed ${BORDER_BLUE}`, background: LIGHT_BLUE, textAlign: "center", cursor: "pointer", fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: TEXT_DARK }}>
                  📷 {profileUploading ? "Uploading..." : "Click to upload a photo"}
                  <input type="file" accept="image/*" onChange={handleProfilePhoto} style={{ display: "none" }} disabled={profileUploading} />
                </label>
                {profileUploadError && <div style={{ color: "#cc0000", fontSize: 11, marginTop: 4, fontFamily: "'Trebuchet MS', sans-serif" }}>{profileUploadError}</div>}
                {s.profile_photo && (
                  <button onClick={() => update("profile_photo", null)} style={{ marginTop: 4, background: "none", border: "none", color: "#cc0000", fontSize: 11, cursor: "pointer", fontFamily: "'Trebuchet MS', sans-serif" }}>✕ Remove photo (use emoji instead)</button>
                )}
              </div>
              <AdminField label="About Me" field="bio" multiline value={s.bio} onChange={update} />
              <AdminField label="Blinkies (comma separated)" field="blinkies" value={s.blinkies} onChange={update} />
            </>
          )}
          {activeTab === "page" && (
            <>
              <AdminField label="Site Name" field="site_name" value={s.site_name} onChange={update} />
              <AdminField label="Subtitle" field="subtitle" value={s.subtitle} onChange={update} />
              <AdminField label="Marquee / Scrolling Text" field="marquee" multiline value={s.marquee} onChange={update} />
            </>
          )}
          {activeTab === "song" && (
            <>
              <AdminField label="Song Title" field="song_title" value={s.song_title} onChange={update} />
              <AdminField label="Artist" field="song_artist" value={s.song_artist} onChange={update} />
              <AdminField label="Spotify Link" field="song_url" placeholder="https://open.spotify.com/track/..." value={s.song_url} onChange={update} />
              <div style={{ marginTop: 8, padding: 8, background: "#fff8e0", border: "1px solid #cc9900", fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11 }}>
                💡 <b>Tip:</b> Go to Spotify → find your song → click "..." → Share → Copy Song Link → paste it above!
              </div>
            </>
          )}
          {activeTab === "appts" && (
            <>
              <AdminField label="Upcoming Appointments" field="upcoming_appointments" multiline placeholder={"One per line, e.g.\nNov 4 - Oncology check-up\nNov 12 - Scan results\nNov 20 - Infusion #3"} value={s.upcoming_appointments} onChange={update} />
              <div style={{ marginTop: 8, padding: 8, background: "#fff8e0", border: "1px solid #cc9900", fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11 }}>
                💡 <b>Tip:</b> Put each appointment on its own line. Leave blank to hide the box from visitors.
              </div>
            </>
          )}
          <button onClick={handleSave}
            style={{ width: "100%", padding: 8, marginTop: 12, border: `2px outset ${saved ? "#339966" : MYSPACE_ORANGE}`, background: saved ? "#339966" : MYSPACE_ORANGE, color: WHITE, fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, fontWeight: 800, cursor: "pointer", textTransform: "uppercase", letterSpacing: 1 }}>
            {saved ? "✓ SAVED!" : "💾 SAVE CHANGES"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===================== SUBSCRIBE BOX ===================== */
function SubscribeBox() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async () => {
    if (!name.trim() || !email.trim()) { setError("Please enter your name and email"); return; }
    if (!email.includes("@")) { setError("Please enter a valid email"); return; }
    setSubmitting(true);

    if (supabase) {
      const { error: dbError } = await supabase.from('subscribers').insert({
        name: name.trim(),
        contact: email.trim(),
        type: 'email',
      });
      if (dbError && dbError.code === '23505') {
        setError("You're already subscribed!");
        setSubmitting(false);
        return;
      }
      // Sync to SendGrid contacts
      await supabase.functions.invoke('sync-contact', {
        body: { name: name.trim(), email: email.trim() }
      });
    }
    setSubscribed(true);
    setName("");
    setEmail("");
    setError("");
    setSubmitting(false);
  };

  if (subscribed) {
    return (
      <div style={{ textAlign: "center", fontFamily: "'Comic Neue', cursive", fontSize: 13, color: "#339966" }}>
        ✓ You're in! You'll get notified when there's a new update. 💙🌊
      </div>
    );
  }

  return (
    <>
      <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: TEXT_BODY, marginBottom: 6 }}>Get notified when I post:</div>
      <input type="text" placeholder="Your name" value={name} onChange={e => { setName(e.target.value); setError(""); }}
        style={{ width: "100%", padding: "4px 6px", border: `2px inset ${BORDER_BLUE}`, fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, marginBottom: 6, boxSizing: "border-box" }} />
      <input type="email" placeholder="Your email" value={email} onChange={e => { setEmail(e.target.value); setError(""); }}
        onKeyDown={e => { if (e.key === "Enter") handleSubscribe(); }}
        style={{ width: "100%", padding: "4px 6px", border: `2px inset ${BORDER_BLUE}`, fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, marginBottom: 6, boxSizing: "border-box" }} />
      {error && <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 10, color: "#cc0000", marginBottom: 4 }}>{error}</div>}
      <button onClick={handleSubscribe} disabled={submitting}
        style={{ width: "100%", padding: 6, border: `2px outset ${MYSPACE_ORANGE}`, background: MYSPACE_ORANGE, color: WHITE, fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, fontWeight: 700, cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.7 : 1 }}>📧 {submitting ? "Subscribing..." : "Subscribe for Updates"}</button>
    </>
  );
}

/* ===================== MAIN APP ===================== */
function App() {
  const [showCompose, setShowCompose] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  // Load data on mount
  useEffect(() => {
    loadData();
    checkAdmin();
  }, []);

  const checkAdmin = async () => {
    if (!supabase) { setIsAdmin(true); return; } // Default to admin in dev
    const { data: { user } } = await supabase.auth.getUser();
    setIsAdmin(!!user);
  };

  const handleLogin = async () => {
    setLoginError("");
    const { error } = await supabase.auth.signInWithPassword({ email: loginEmail, password: loginPassword });
    if (error) {
      setLoginError("Invalid email or password.");
    } else {
      setIsAdmin(true);
      setShowLogin(false);
      setLoginEmail("");
      setLoginPassword("");
    }
  };

  const loadData = async () => {
    if (!supabase) {
      // Fallback sample data when no Supabase
      setPosts(SAMPLE_POSTS);
      setLoading(false);
      return;
    }
    // Load settings
    const { data: settingsData } = await supabase.from('settings').select('*').single();
    if (settingsData) setSettings(settingsData);
    // Load posts
    const { data: postsData } = await supabase.from('posts').select('*').order('created_at', { ascending: false });
    if (postsData) setPosts(postsData);
    setLoading(false);
  };

  const handleNewPost = async ({ type, title, content, notify, imageUrl }) => {
    const post = {
      type, title, content: content || "", image_url: imageUrl || null, pinned: false,
      reactions: { "💙": 0, "🙏": 0, "💪": 0, "🌊": 0, "🤗": 0 },
      created_at: new Date().toISOString(),
    };
    if (supabase) {
      const { data } = await supabase.from('posts').insert(post).select();
      if (data) setPosts([data[0], ...posts]);
      // Trigger notifications via edge function
      if (notify && (notify === 'sms' || notify === 'both' || notify === 'email')) {
        await supabase.functions.invoke('send-notifications', {
          body: { postId: data?.[0]?.id, title, type: notify }
        });
      }
    } else {
      setPosts([{ ...post, id: Date.now() }, ...posts]);
    }
    setShowCompose(false);
  };

  const handleSaveSettings = async (newSettings) => {
    setSettings(newSettings);
    if (supabase) {
      await supabase.from('settings').upsert(newSettings);
    }
  };

  const handleDeletePost = async (postId) => {
    setPosts(prev => prev.filter(p => p.id !== postId));
    if (supabase) {
      const { error } = await supabase.from('posts').delete().eq('id', postId);
      if (error) {
        alert("Couldn't delete post: " + error.message);
        // Refresh to re-sync with DB
        const { data } = await supabase.from('posts').select('*').order('created_at', { ascending: false });
        if (data) setPosts(data);
      }
    }
  };

  const handlePinPost = async (postId, pinned) => {
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, pinned } : p));
    if (supabase) {
      const { error } = await supabase.from('posts').update({ pinned }).eq('id', postId);
      if (error) {
        alert("Couldn't update pin: " + error.message);
        const { data } = await supabase.from('posts').select('*').order('created_at', { ascending: false });
        if (data) setPosts(data);
      }
    }
  };

  const handleReactionUpdate = (postId, newReactions) => {
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, reactions: newReactions } : p));
  };

  const filteredPosts = (filter === "all" ? posts : posts.filter(p => p.type === filter))
    .slice().sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
  const blinkiesArray = typeof settings.blinkies === 'string' ? settings.blinkies.split(",").map(b => b.trim()).filter(Boolean) : settings.blinkies || [];

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#1a1a2e", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🌊</div>
          <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 16, color: "#6BC4E8" }}>Loading Waves Upon Waves...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="waves-app">
      <link href="https://fonts.googleapis.com/css2?family=Comic+Neue:wght@400;700&display=swap" rel="stylesheet" />

      {/* Header */}
      <div style={{ background: `linear-gradient(180deg, ${BG_DARK}, #224477)`, borderBottom: `3px outset ${BORDER_BLUE}`, padding: "8px 16px" }}>
        <div style={{ maxWidth: 860, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <div>
            <div style={{ fontFamily: "'Impact', 'Arial Black', sans-serif", fontSize: 24, color: WHITE, letterSpacing: -0.5 }}>
              <span style={{ color: "#6BC4E8" }}>🌊</span> {settings.site_name}
            </div>
            <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: LIGHT_BLUE, marginTop: 1 }}>{settings.subtitle}</div>
          </div>
          {isAdmin && (
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => setShowAdmin(true)}
                style={{ padding: "4px 12px", border: `2px outset ${BORDER_BLUE}`, background: HEADER_BLUE, color: WHITE, fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>⚙️ Edit My Page</button>
              <button onClick={() => setShowCompose(true)}
                style={{ padding: "4px 12px", border: `2px outset ${MYSPACE_ORANGE}`, background: MYSPACE_ORANGE, color: WHITE, fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>✏️ New Bulletin</button>
              <button onClick={async () => { if (supabase) await supabase.auth.signOut(); setIsAdmin(false); }}
                style={{ padding: "4px 12px", border: `2px outset #999`, background: "#666", color: WHITE, fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Logout</button>
            </div>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 860, margin: "0 auto", padding: "12px 16px" }}>
        {/* Admin stats */}
        {isAdmin && (
          <div style={{ background: "#222", border: `2px outset #555`, padding: "6px 12px", marginBottom: 12, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <span style={{ fontFamily: "'Courier New', monospace", fontSize: 11, color: "#0f0" }}>📝 {posts.length} posts</span>
            <span style={{ fontFamily: "'Courier New', monospace", fontSize: 11, color: "#0f0" }}>💙 {posts.reduce((acc, p) => acc + Object.values(p.reactions || {}).reduce((a, b) => a + b, 0), 0)} reactions</span>
          </div>
        )}

        {/* Marquee */}
        <div style={{ background: "#000", border: `2px inset ${BORDER_BLUE}`, padding: "4px 8px", marginBottom: 12, overflow: "hidden" }}>
          <MarqueeText>{settings.marquee}</MarqueeText>
        </div>

        {/* Main Layout */}
        <div className="main-layout" style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
          {/* Left Column */}
          <div className="left-column" style={{ width: 240, flexShrink: 0 }}>
            <SectionBox title={`${settings.display_name}'s Profile`}>
              <div style={{ textAlign: "center" }}>
                {settings.profile_photo ? (
                  <img src={settings.profile_photo} alt={settings.display_name} style={{ width: 160, height: 160, margin: "0 auto 8px", border: `3px solid ${BORDER_BLUE}`, objectFit: "cover", display: "block" }} />
                ) : (
                  <div style={{ width: 160, height: 160, margin: "0 auto 8px", border: `3px solid ${BORDER_BLUE}`, background: `linear-gradient(135deg, #e3f4fb, #c0ddf5)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 64 }}>{settings.profile_emoji}</div>
                )}
                <div style={{ fontFamily: "'Comic Neue', cursive", fontSize: 18, fontWeight: 700, color: TEXT_DARK, marginBottom: 2 }}>{settings.display_name}</div>
                <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: "#666" }}>{settings.tagline}</div>
                <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: "#999", marginTop: 2 }}>{settings.location}</div>
                <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, color: "#999" }}>Last Login: Today</div>
                <div style={{ marginTop: 8, display: "flex", gap: 4, justifyContent: "center", flexWrap: "wrap" }}>
                  {blinkiesArray.map((b, i) => <Blinkie key={i} text={b} bg={["#ff69b4", "#6699cc", "#003366", "#993366", "#339966"][i % 5]} color="#fff" />)}
                </div>
              </div>
            </SectionBox>

            {settings.upcoming_appointments && settings.upcoming_appointments.trim() && (
              <SectionBox title="📅 Upcoming Appointments" color="#339966">
                <div style={{ fontFamily: "'Courier New', monospace", fontSize: 12, color: TEXT_DARK, lineHeight: 1.7 }}>
                  {settings.upcoming_appointments.split("\n").filter(l => l.trim()).map((line, i) => (
                    <div key={i} style={{ padding: "3px 0", borderBottom: i < settings.upcoming_appointments.split("\n").filter(l => l.trim()).length - 1 ? "1px dashed #99ccaa" : "none", display: "flex", alignItems: "flex-start", gap: 6 }}>
                      <span style={{ color: "#339966", fontWeight: 700 }}>▸</span>
                      <span>{line}</span>
                    </div>
                  ))}
                </div>
              </SectionBox>
            )}

            <SectionBox title="About Me" color="#993366">
              <div style={{ fontFamily: "'Comic Neue', cursive", fontSize: 12, color: TEXT_BODY, lineHeight: 1.6, whiteSpace: "pre-line" }}>{settings.bio}</div>
            </SectionBox>

            <SectionBox title="Subscribe!" color={MYSPACE_ORANGE}>
              <SubscribeBox />
            </SectionBox>
          </div>

          {/* Right Column */}
          <div className="right-column" style={{ flex: 1, minWidth: 300 }}>
            <SectionBox title="🎵 Now Playing">
              <MusicPlayer settings={settings} />
            </SectionBox>
            <SectionBox title={`${settings.display_name}'s Bulletins`}>
              <div style={{ display: "flex", gap: 4, marginBottom: 10, flexWrap: "wrap" }}>
                {[{ key: "all", label: "All" }, { key: "update", label: "Updates" }, { key: "photo", label: "Photos" }, { key: "support", label: "Ways to Support" }].map(tab => (
                  <button key={tab.key} onClick={() => setFilter(tab.key)}
                    style={{ padding: "3px 10px", border: filter === tab.key ? `2px inset ${MYSPACE_ORANGE}` : `2px outset ${BORDER_BLUE}`, background: filter === tab.key ? "#fff3e0" : LIGHT_BLUE, fontFamily: "'Trebuchet MS', sans-serif", fontSize: 11, fontWeight: 700, color: filter === tab.key ? MYSPACE_ORANGE : TEXT_DARK, cursor: "pointer" }}>{tab.label}</button>
                ))}
              </div>
              {filteredPosts.map(post => <BulletinPost key={post.id} post={post} isAdmin={isAdmin} onDelete={handleDeletePost} onUpdate={handleReactionUpdate} onPin={handlePinPost} />)}
              {filteredPosts.length === 0 && <div style={{ textAlign: "center", padding: 20, fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, color: "#999" }}>No bulletins here yet!</div>}
            </SectionBox>

            <SectionBox title="🌊 Wave Counter" color="#339966">
              <div style={{ textAlign: "center" }}>
                <div style={{ fontFamily: "'Impact', sans-serif", fontSize: 36, color: HEADER_BLUE }}>
                  {posts.reduce((acc, p) => acc + Object.values(p.reactions || {}).reduce((a, b) => a + b, 0), 0)}
                </div>
                <div style={{ fontFamily: "'Comic Neue', cursive", fontSize: 12, color: "#666" }}>waves of love sent 💙</div>
              </div>
            </SectionBox>
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: "center", padding: "20px 10px", marginTop: 12 }}>
          <div style={{ fontFamily: "'Comic Neue', cursive", fontSize: 12, color: LIGHT_BLUE }}>
            <span style={{ color: GLITTER_PINK }}>~*~*~</span> {settings.site_name} © 2026 <span style={{ color: GLITTER_PINK }}>~*~*~</span>
          </div>
          <div style={{ marginTop: 8 }}>
            {isAdmin ? (
              <button onClick={async () => { await supabase.auth.signOut(); setIsAdmin(false); }}
                style={{ background: "none", border: "none", color: "#555", fontSize: 11, cursor: "pointer", fontFamily: "'Trebuchet MS', sans-serif" }}>
                🔓 Sign out
              </button>
            ) : (
              <button onClick={() => setShowLogin(true)}
                style={{ background: "none", border: "none", color: "#444", fontSize: 11, cursor: "pointer", fontFamily: "'Trebuchet MS', sans-serif" }}>
                🔒
              </button>
            )}
          </div>
        </div>
      </div>

      {showCompose && <ComposeModal onClose={() => setShowCompose(false)} onPost={handleNewPost} />}
      {showAdmin && <AdminPanel settings={settings} onSave={handleSaveSettings} onClose={() => setShowAdmin(false)} />}
      {showLogin && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#1a2a3a", border: `2px outset ${BORDER_BLUE}`, padding: 24, minWidth: 280 }}>
            <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontWeight: 700, color: WHITE, marginBottom: 16 }}>🔒 Admin Login</div>
            <input type="email" placeholder="Email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)}
              style={{ width: "100%", padding: "6px 8px", marginBottom: 8, background: "#0a1a2a", border: `1px solid ${BORDER_BLUE}`, color: WHITE, fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, boxSizing: "border-box" }} />
            <input type="password" placeholder="Password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleLogin()}
              style={{ width: "100%", padding: "6px 8px", marginBottom: 12, background: "#0a1a2a", border: `1px solid ${BORDER_BLUE}`, color: WHITE, fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, boxSizing: "border-box" }} />
            {loginError && <div style={{ color: "#ff6666", fontSize: 12, marginBottom: 8, fontFamily: "'Trebuchet MS', sans-serif" }}>{loginError}</div>}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleLogin}
                style={{ flex: 1, padding: "6px 12px", border: `2px outset ${BORDER_BLUE}`, background: HEADER_BLUE, color: WHITE, fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                Login
              </button>
              <button onClick={() => { setShowLogin(false); setLoginError(""); }}
                style={{ padding: "6px 12px", border: `2px outset #555`, background: "#333", color: WHITE, fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
